import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { isPastPrivateEvent } from '../../api/privateEvents';
import { useViewport } from '../../hooks/useViewport';
import { DashboardsPage } from '../dashboards/DashboardsPage';
import { SegmentedControl } from '../dashboards/primitives';

import CreatePrivateEventDrawer from './CreatePrivateEventDrawer';
import { privateEventReturnState } from './private-event-return';
import { privateEventsErrorMessage } from './private-events-error-message';
import PrivateEventsCardList from './PrivateEventsCardList';
import PrivateEventsEmpty from './PrivateEventsEmpty';
import PrivateEventsPageHeader from './PrivateEventsPageHeader';
import { PrivateEventsScopePicker } from './PrivateEventsScopePicker';
import PrivateEventsTable from './PrivateEventsTable';
import { usePrivateEventsList } from './usePrivateEventsList';

/**
 * Private events (BON-518) — the landing surface in the Workshops section.
 *
 * Scope for this slice: list what the caller may see, and create one at a
 * partner venue. Client-site addresses (BON-519), staffing (BON-520), editing
 * (BON-521) and cancellation (BON-522) each add to this page later.
 *
 * The list is deliberately NOT filtered client-side by region — the server
 * decides visibility, because the rule is not purely regional: an RD also sees
 * an event they own outside their own regions. The region picker sends
 * `?region=` to the server, which can only narrow that set.
 *
 * Everything here is styled through `DashboardsPage` and the `--nb-*` tokens
 * rather than raw Tailwind colours. That is not cosmetic: the app renders in a
 * dark theme, so a hardcoded `bg-white` card is a white slab in a dark page and
 * `text-gray-900` on the heading is very nearly invisible. Both were true of the
 * first cut of this page.
 */
type Tab = 'upcoming' | 'past';

const TABS = [
  { value: 'upcoming' as const, label: 'Upcoming' },
  { value: 'past' as const, label: 'Past' },
];

export default function PrivateEventsPage() {
  const [params] = useSearchParams();
  const regionSlug = params.get('region') || undefined;
  const { events, visibleRegionIds, error, reload } = usePrivateEventsList(regionSlug);
  const { isPhone } = useViewport();
  const [tab, setTab] = useState<Tab>('upcoming');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const onOpen = (event: { id: string }) =>
    navigate(event.id, { state: privateEventReturnState(pathname + search, 'Private events') });

  const visible = useMemo(() => {
    if (!events) return [];
    const past = tab === 'past';
    return events.filter((e) => isPastPrivateEvent(e) === past);
  }, [events, tab]);

  return (
    <DashboardsPage
      header={
        <PrivateEventsPageHeader
          onCreate={() => setIsCreateOpen(true)}
          scope={
            <PrivateEventsScopePicker
              regionSlug={regionSlug}
              visibleRegionIds={visibleRegionIds}
              isPhone={isPhone}
            />
          }
        />
      }
    >
      <SegmentedControl
        value={tab}
        onChange={setTab}
        options={TABS}
        ariaLabel="Upcoming or past private events"
      />

      {error !== null && (
        <p
          role="alert"
          className="nb-card nb-card-tight"
          style={{ margin: 0, fontSize: 12, color: 'var(--nb-risk)' }}
        >
          {privateEventsErrorMessage(error)}
        </p>
      )}

      {events === null && error === null && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>Loading…</p>
      )}

      {events !== null &&
        (visible.length === 0 ? (
          <PrivateEventsEmpty tab={tab} onCreate={() => setIsCreateOpen(true)} />
        ) : isPhone ? (
          // Not a narrower table: at 375px the six columns scroll off-screen and
          // take headcount, staffing and deal value with them.
          <PrivateEventsCardList events={visible} onOpen={onOpen} />
        ) : (
          <PrivateEventsTable events={visible} onOpen={onOpen} />
        ))}

      {isCreateOpen && (
        <CreatePrivateEventDrawer
          onClose={() => setIsCreateOpen(false)}
          onCreated={() => {
            setIsCreateOpen(false);
            void reload();
          }}
        />
      )}
    </DashboardsPage>
  );
}
